import { OnInit, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  BaseCrudComponent,
  createImportExcelModalConfig,
  CrudPage,
  ExcelImportErrorsExportLike,
  ExcelImportResultLike,
  GenericForm,
  IMPORT_MAX_SIZE_MB,
  IActionsSchema,
  ICrudService,
  ModalBuilder,
  resolveActions,
  ZnFileUploader,
  ZnExcelImportErrorsModal,
} from '@zendra/shared';
import {
  InventoryDataEntryCreateDto,
  InventoryDataEntryUpdateDto,
} from '../proxy/inventory/dtos/inventory-data-entry';
import { InventoryDataEntryService } from '../proxy/inventory/controllers';
import {
  INVENTORY_DATA_ENTRY_ACTION_IDS,
  INVENTORYDATAENTRY_ACTIONS,
  INVENTORYDATAENTRY_CONFIG,
  INVENTORYDATAENTRY_FIELDS,
} from './inventory-data-entry.config';
import { InventoryDataEntryPlanningOrchestratorService } from 'orchestrator';
import {
  GetInventoryDataEntryProjectionListInputDto,
  InventoryDataEntryProjectionDto,
  InventoryDataEntryProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/inventory-data-entries/models';

type InventoryDataEntryImportResult = ExcelImportResultLike<unknown> & {
  fileName?: string;
};

@Component({
  selector: 'app-inventory-data-entry',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
  ],
  providers: [ListService, InventoryDataEntryService, ConfirmationService],
  templateUrl: './inventory-data-entry.html',
})
export class InventoryDataEntry extends BaseCrudComponent<
  InventoryDataEntryProjectionDto,
  InventoryDataEntryCreateDto,
  InventoryDataEntryUpdateDto,
  InventoryDataEntryProjectionExcelDownloadDto
> implements OnInit {
  private readonly inventoryService = inject(InventoryDataEntryService);
  private readonly orchestratorService = inject(InventoryDataEntryPlanningOrchestratorService);
  private readonly modalService = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);
  service: ICrudService<
    InventoryDataEntryProjectionDto,
    InventoryDataEntryCreateDto,
    InventoryDataEntryUpdateDto,
    InventoryDataEntryProjectionExcelDownloadDto
  > = {
    create: input => this.inventoryService.create(input),
    update: (id, input) => this.inventoryService.update(id, input),
    deleteByIds: ids => this.inventoryService.deleteByIds(ids),
    getList: (query: GetInventoryDataEntryProjectionListInputDto) =>
      this.orchestratorService.getList(query),
    getListByIds: input => this.inventoryService.getListByIds(input),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(input),
  };
  config = INVENTORYDATAENTRY_CONFIG;
  fields = INVENTORYDATAENTRY_FIELDS;
  actions: IActionsSchema | null = null;

  private readonly triggerMap: Record<number, () => void> = {
    [INVENTORY_DATA_ENTRY_ACTION_IDS.importExcelModal]: () => this.openImportModal(),
    [INVENTORY_DATA_ENTRY_ACTION_IDS.downloadExcelTemplateTrigger]: () => this.downloadExcelTemplate(),
  };

  ngOnInit(): void {
    this.actions = this.withGridRefreshOnActionCompleted(resolveActions(INVENTORYDATAENTRY_ACTIONS, this.triggerMap));
  }

  protected prepareUpdateDto(data: any, selected: InventoryDataEntryProjectionDto): InventoryDataEntryUpdateDto {
    return {
      measurementDate: data.measurementDate,
      standNumber: data.standNumber,
      species: data.species,
      plotNumber: data.plotNumber,
      treeNumber: data.treeNumber,
      height: data.height,
      averageHeightDiameter: data.averageHeightDiameter,
      heightDiameterFirstMeasurement: data.heightDiameterFirstMeasurement,
      heightDiameterSecondMeasurement: data.heightDiameterSecondMeasurement,
      radius: data.radius,
      meditionNumber: data.meditionNumber,
      isProcessed: data.isProcessed,
      shouldPick: data.shouldPick,
      betweenLines1: data.betweenLines1,
      betweenLines2: data.betweenLines2,
      betweenLines3: data.betweenLines3,
      measurementOrderNumber: data.measurementOrderNumber,
      teamId: data.teamId,
      treeObservationId: data.treeObservationId,
      inventorySubTypeId: data.inventorySubTypeId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'InventoryDataEntry';
  }

  private openImportModal(): void {
    const modalRef = this.modalService.openModal(
      ZnFileUploader,
      createImportExcelModalConfig({
        component: ZnFileUploader,
        title: 'Inventory::Action.Modal.ImportMeasurementInventoryDataEntry.Title',
        subTitle: 'Inventory::Entity.InventoryDataEntry',
        inputs: {
          maxSizeMb: IMPORT_MAX_SIZE_MB,
          importFile: (formData: FormData) =>
            this.orchestratorService.importInventoryDataEntryMeasurement(formData),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: InventoryDataEntryImportResult) => {
        if (result?.hasErrors) {
          this.openImportErrorsModal(result);
          return;
        }

        if (result) {
          this.refreshGrid();
        }
      });
  }

  private openImportErrorsModal(result: InventoryDataEntryImportResult): void {
    const modalRef = this.modalService.openModal(
      ZnExcelImportErrorsModal,
      createImportExcelModalConfig({
        component: ZnExcelImportErrorsModal,
        title: 'LanguageService::Action:ExcelImport.Title',
        subTitle: 'Inventory::Entity.InventoryDataEntry',
        confirmLabel: 'LanguageService::Action:ExcelImport.ImportAgain',
        dismissLabel: 'LanguageService::Close',
        size: 'lg',
        inputs: {
          fileName: result.fileName ?? '',
          errors: result.errors,
          title: 'LanguageService::Action:ExcelImport.Title',
          subTitle: 'Inventory::Entity.InventoryDataEntry',
          exportErrors: (input: ExcelImportErrorsExportLike) =>
            this.orchestratorService.exportInventoryDataEntryImportErrors(input),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((modalResult?: { retry?: boolean }) => {
        if (modalResult?.retry) {
          this.openImportModal();
        }
      });
  }

  private downloadExcelTemplate(): void {
    this.orchestratorService.getInventoryDataEntryImportTemplate().subscribe({
      next: result => {
        this.toaster.success('LanguageService::Actions:DownloadTemplateMessage', null, { life: 5000 });
        this.downloadFile(result, 'continuous-inventory-import-template');
      },
      error: error => {
        this.toaster.error(error, null, { life: 5000 });
      },
    });
  }
}
